import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import Carousel from "../Carousel";
import Swal from "sweetalert2";
import Apiurl from "../../Helpers/apiUrl";
import {
  Box,
  LinearProgress,
  Typography,
  IconButton,
  InputAdornment,
  TextField,
  Button,
  Paper,
  Stack,
  Chip,
  Fade,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  CheckCircle,
  Error,
} from "@mui/icons-material";
import { handleApiError } from "../../Helpers/handleApiError ";
import img1 from "../../../src/SliderImage/1.jpg";
import img3 from "../../../src/SliderImage/3.jpg";
import img4 from "../../../src/SliderImage/4.jpg";
import img5 from "../../../src/SliderImage/5.jpg";
import img6 from "../../../src/SliderImage/6.jpg";
import img7 from "../../../src/SliderImage/7.jpg";
import img8 from "../../../src/SliderImage/8.jpg";

const images = [img1, img3, img4, img5, img6, img7, img8];

function ResetPassword() {
  const { token } = useParams();
  const { page } = useParams();
  const [password, setPassword] = useState("");
  const [confirmed, setConfirmed] = useState("");
  const [userToken, setUserToken] = useState("");
  const [email, setEmail] = useState("");
  const [showPassword, setShowPassword] = useState({
    password: false,
    confirmPassword: false,
  });
  const [passwordStatus, setPasswordStatus] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    digit: false,
    specialChar: false,
    match: false,
  });
  const [showStatusBox, setShowStatusBox] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);

  useEffect(() => {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const param2 = urlParams.get("email");
    setUserToken(token);
    setEmail(param2);
  }, [token]);

  const validatePassword = useCallback(() => {
    const status = {
      length: password.length >= 14,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      digit: /[0-9]/.test(password),
      specialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
      match: password === confirmed && password.length > 0,
    };
    setPasswordStatus(status);
    const isValid = Object.values(status).every((val) => val);
    setIsFormValid(isValid);
    return isValid;
  }, [password, confirmed]);

  useEffect(() => {
    validatePassword();
  }, [validatePassword]);

  const handleClickShowPassword = (field) => {
    setShowPassword((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const updatePassword = async (e) => {
    e.preventDefault();

    if (!isFormValid) {
      Swal.fire({
        icon: "error",
        title: "Password Requirements Not Met",
        html: `
          <div style="text-align: left;">
            <p>Your password must contain:</p>
            <ul>
              <li>At least 14 characters</li>
              <li>Uppercase letter</li>
              <li>Lowercase letter</li>
              <li>Number</li>
              <li>Special character</li>
              <li>Matching confirmation</li>
            </ul>
          </div>
        `,
      });
      return;
    }

    try {
      const data = {
        email,
        token,
        password,
        confirmed,
      };
      const response = await axios.post(`${Apiurl}reset-password`, data);
      if (response.status) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Password updated successfully",
          showConfirmButton: true,
          confirmButtonText: "OK",
        }).then((result) => {
          if (result.isConfirmed) {
            window.location.href = "/";
          }
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "15 mintues after try again.",
          timer: 3000,
          showConfirmButton: true,
          confirmButtonText: "Retry",
        });
      }
    } catch (error) {
      handleApiError(error, "Failed to reset password. Please try again.");
    }
  };

  const passwordStrength = () => {
    const requirementsMet = Object.values(passwordStatus)
      .slice(0, 5) // Only count the first 5 requirements (exclude match)
      .filter((val) => val).length;
    return (requirementsMet / 5) * 100;
  };

  return (
    <>
      <Carousel images={images} />

      <Box
        sx={{
          maxWidth: "500px",
          margin: "0 auto",
          padding: "2rem",
          borderRadius: "8px",
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          boxShadow: 3,
          position: "relative",
          mt: 20,
        }}
      >
        <Typography
          variant="h5"
          component="h1"
          gutterBottom
          align="center"
          sx={{
            fontWeight: 600,
            color: "primary.main",
            mb: 3,
          }}
        >
          Reset Password
        </Typography>

        <Box component="form" onSubmit={updatePassword} sx={{ mt: 3 }}>
          <TextField
            fullWidth
            label="New Password"
            type={showPassword.password ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onFocus={() => setShowStatusBox(true)}
            onBlur={() => setShowStatusBox(false)}
            required
            sx={{
              mb: 2,
              //   "& .MuiOutlinedInput-root": {
              //     borderRadius: "12px",
              //   }
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => handleClickShowPassword("password")}
                    edge="end"
                    sx={{ color: "text.secondary" }}
                  >
                    {showPassword.password ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Fade in={showStatusBox || password.length > 0}>
            <Paper
              elevation={3}
              sx={{
                position: "absolute",
                right: { xs: 0, sm: -280 },
                top: 120,
                width: 250,
                p: 2,
                borderRadius: "12px",
                backgroundColor: "background.paper",
                zIndex: 1,
                display: { xs: "none", sm: "block" },
              }}
            >
              <Typography variant="subtitle2" gutterBottom>
                Password Requirements
              </Typography>
              <Stack spacing={1}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  {passwordStatus.length ? (
                    <CheckCircle color="success" fontSize="small" />
                  ) : (
                    <Error color="error" fontSize="small" />
                  )}
                  <Typography variant="body2" sx={{ ml: 1 }}>
                    Minimum 14 characters
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  {passwordStatus.uppercase ? (
                    <CheckCircle color="success" fontSize="small" />
                  ) : (
                    <Error color="error" fontSize="small" />
                  )}
                  <Typography variant="body2" sx={{ ml: 1 }}>
                    Uppercase letter
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  {passwordStatus.lowercase ? (
                    <CheckCircle color="success" fontSize="small" />
                  ) : (
                    <Error color="error" fontSize="small" />
                  )}
                  <Typography variant="body2" sx={{ ml: 1 }}>
                    Lowercase letter
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  {passwordStatus.digit ? (
                    <CheckCircle color="success" fontSize="small" />
                  ) : (
                    <Error color="error" fontSize="small" />
                  )}
                  <Typography variant="body2" sx={{ ml: 1 }}>
                    Number
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  {passwordStatus.specialChar ? (
                    <CheckCircle color="success" fontSize="small" />
                  ) : (
                    <Error color="error" fontSize="small" />
                  )}
                  <Typography variant="body2" sx={{ ml: 1 }}>
                    Special character
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Fade>

          <Box sx={{ mb: 2 }}>
            <LinearProgress
              variant="determinate"
              value={passwordStrength()}
              sx={{
                height: 8,
                borderRadius: 4,
              }}
              color={
                passwordStrength() < 50
                  ? "error"
                  : passwordStrength() < 80
                  ? "warning"
                  : "success"
              }
            />
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mt: 0.5,
              }}
            >
              <Typography variant="caption">Password strength</Typography>
              <Chip
                label={
                  passwordStrength() < 50
                    ? "Weak"
                    : passwordStrength() < 80
                    ? "Good"
                    : "Strong"
                }
                size="small"
                color={
                  passwordStrength() < 50
                    ? "error"
                    : passwordStrength() < 80
                    ? "warning"
                    : "success"
                }
              />
            </Box>
          </Box>

          <TextField
            fullWidth
            label="Confirm Password"
            type={showPassword.confirmPassword ? "text" : "password"}
            value={confirmed}
            onChange={(e) => setConfirmed(e.target.value)}
            required
            error={confirmed.length > 0 && !passwordStatus.match}
            helperText={
              confirmed.length > 0 && !passwordStatus.match
                ? "Passwords do not match"
                : passwordStatus.match
                ? "Passwords match"
                : ""
            }
            sx={{
              mb: 2,
              //   "& .MuiOutlinedInput-root": {
              //     borderRadius: "12px",
              //   }
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => handleClickShowPassword("confirmPassword")}
                    edge="end"
                    sx={{ color: "text.secondary" }}
                  >
                    {showPassword.confirmPassword ? (
                      <VisibilityOff />
                    ) : (
                      <Visibility />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            sx={{
              mt: 2,
              py: 1.5,
              borderRadius: "12px",
              fontWeight: 600,
              textTransform: "none",
              fontSize: "1rem",
              boxShadow: "none",
              "&:hover": {
                boxShadow: "none",
              },
            }}
            disabled={!isFormValid}
          >
            Reset Password
          </Button>
        </Box>
      </Box>
    </>
  );
}

export default ResetPassword;
